import React, { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import _ from 'lodash';

import './LottoLobby.scss';
import { RootState } from '../../configs/store';
import SearchResults from '../../components/SearchResults/SearchResults';
import Page from '../../containers/Page/Page';
import Tile from '../../components/NumbersBetting/Tile/Tile';
import usePreviousValue from 'src/hooks/usePreviousValue';
import useIsMobile from 'src/hooks/useIsMobile';
import FastMode from 'src/components/HorseBetting/FastMode/FastMode';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import RowTile from 'src/components/NumbersBetting/RowTile/RowTile';
import Icon from 'src/components/Icon/Icon';
import classNames from 'classnames';

function LottoLobby() {
  const { popularEvents, nextEvents } = useSelector((state: RootState) => state.lotto);
  const { isSearching } = useSelector((state: RootState) => state.events);
  const { configs: { lotto } } = useSelector((state: RootState) => state.content);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const mobile = useIsMobile();
  const query = new URLSearchParams(location.search);
  const prevValue = usePreviousValue(query.get('card'));
  const [visiblePopular, setVisiblePopular] = useState(true);
  const [visibleNext, setVisibleNext] = useState(true);

  const { RETAIL_MODE } = useContext(AppModeContext);

  useEffect(() => {
    const getData = async () => {
      await dispatch.lotto.getNumbersEvents({ hideLoading: true, module: 64, hours: -1  });
    };
    getData();

    const refreshInterval = setInterval(() => {
      getData();
    }, 120000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    if (query.has('card') && prevValue !== query.get('card')) {
      const card = query.get('card');
      if (card === '9') {
        dispatch.peripherals.getLastMatrix();
      } else {
        dispatch.peripherals.getCardbyId(card);
      }
      dispatch.peripherals.changeBetInfo(true);
    }
  }, [query]);

  const renderNextDraws = (nextDraws) => {
    return (
      <div className="number-bet">
        <div className="number-bet__title-container">
          <div className="number-bet__lobby-title">{ t('numbers.nextDraws') }</div>
          <div className="number-bet__icon-container" onClick={ () => setVisibleNext(!visibleNext) }>
            <Icon
              icon="arrow"
              className={
                classNames({
                  'number-bet__arrow': true,
                  'number-bet__arrow--closed': !visibleNext
                })
              }
            />
          </div>
        </div>
        <div className={
          classNames({
            'number-bet__wrap--next': true,
            'number-bet__wrap': !visibleNext,
            'number-bet__wrap--closed': !visibleNext
          })
        }
        >
          {
            _.map(nextDraws, (next, nextIndex) => (
              !_.isEmpty(next) && <RowTile event={ next } key={ nextIndex } showCodes={ RETAIL_MODE } />
            ))
          }
        </div>
      </div>
    );
  };

  const renderPopularDraws = (popularDraws) => {
    return (
      <div
        className="number-bet number-bet--popular"
      >
        <div className="number-bet__title-container">
          <div className="number-bet__lobby-title">{ t('numbers.popularDraws') }</div>
          <div className="number-bet__icon-container" onClick={ () => setVisiblePopular(!visiblePopular) }>
            <Icon
              icon="arrow"
              className={
                classNames({
                  'number-bet__arrow': true,
                  'number-bet__arrow--closed': !visiblePopular
                })
              }
            />
          </div>
        </div>
        <div className={
          classNames({
            'number-bet__wrap': true,
            'number-bet__wrap--closed': !visiblePopular
          })
        }
        >
          {
            _.map(popularDraws, (popular, popularIndex) => (
              !_.isEmpty(popular) && <Tile event={ popular } key={ popularIndex } showCodes={ RETAIL_MODE } />
            ))
          }
        </div>
      </div>
    );
  };

  return (
    <Page>
      <Helmet>
        <title>BetXchange - Lotto</title>
        <meta name="description" content={ lotto?.description } />
        <meta name="keywords" content={ lotto?.keywords } />
      </Helmet>
      {
        isSearching && !mobile
          ? <SearchResults />
          : (
            <div className="numbers-bet">
              {
                RETAIL_MODE && <FastMode isNumber={ true } >
                  <FastMode.Numbers />
                </FastMode>
              }
              { nextEvents && renderNextDraws(nextEvents) }
              { popularEvents && renderPopularDraws(popularEvents) }
            </div>
          )
      }
    </Page>
  );

}

export default LottoLobby;
